import { useState, useCallback } from 'react';

const STORAGE_KEY = 'starredAircraftTypes';

interface UseStarredAircraftTypesReturn {
  starredTypes: Set<string>;
  isStarred: (code: string) => boolean;
  toggle: (code: string) => void;
  clear: () => void;
}

function loadStarred(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* corrupted — start fresh */ }
  return new Set();
}

function persist(types: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(types)));
}

export function useStarredAircraftTypes(): UseStarredAircraftTypesReturn {
  const [starredTypes, setStarredTypes] = useState<Set<string>>(loadStarred);

  const isStarred = useCallback(
    (code: string) => starredTypes.has(code),
    [starredTypes],
  );

  const toggle = useCallback((code: string) => {
    setStarredTypes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      persist(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    const empty = new Set<string>();
    persist(empty);
    setStarredTypes(empty);
  }, []);

  return { starredTypes, isStarred, toggle, clear };
}
